import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from "@heroui/react";
import { Layers, MoreVertical, Truck } from "lucide-react";
import type { KeyboardEvent } from "react";
import { Link } from "react-router-dom";

import { unauthorizedMessage } from "@/lib/authorization";

import type { ShippingCalendarEntry } from "./api";

/**
 * カレンダーのマスに並べる案件 1 枚。
 *
 * 掴むのはカード全体。一覧の行（[[RowDragHandle]]）はハンドルを分けているが、
 * カードは中に編集するものが無く、掴み損ねる方が困るため全体を掴ませる
 * （工番のリンクとケバブはクリックのままで、ドラッグには取られない）。
 *
 * **出荷済みのカードは掴めない。** 済んだ出荷の予定日を動かしても意味が無く、
 * 記録を書き換えてしまう。
 *
 * ドラッグできない環境のために、フォーカスして ←→ キーで 1 日ずつ動かせる
 * （ケバブからは日付を直接指定できる）。
 */
export default function ShipmentCard({
  entry,
  editable,
  isMoving,
  isLate,
  onDragStart,
  onDragEnd,
  onMoveByDays,
  onEditDate,
}: {
  entry: ShippingCalendarEntry;
  editable: boolean;
  /** 付け替えの送信中。二重に動かさないよう掴めなくする。 */
  isMoving: boolean;
  /** 過去の日に残っている未出荷（遅れている）。 */
  isLate: boolean;
  onDragStart: (entry: ShippingCalendarEntry) => void;
  onDragEnd: () => void;
  onMoveByDays: (entry: ShippingCalendarEntry, days: number) => void;
  onEditDate: (entry: ShippingCalendarEntry) => void;
}) {
  const shipped = entry.shipped_at != null;
  const canMove = editable && !shipped && !isMoving;
  /** 直送なら送り先を見せる（出荷の宛先は直送先）。 */
  const destination = entry.ship_to_name ?? entry.client_name;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!canMove) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    onMoveByDays(entry, event.key === "ArrowLeft" ? -1 : 1);
  };

  const accent = shipped
    ? "border-l-default-300"
    : isLate
      ? "border-l-danger"
      : "border-l-brand-500";

  return (
    <div
      draggable={canMove}
      tabIndex={canMove ? 0 : -1}
      aria-label={`${entry.project_number} の出荷予定`}
      title={
        shipped
          ? "出荷済みのため動かせません"
          : editable
            ? "ドラッグで出荷予定日を変更（←→ キーでも 1 日ずつ動かせます）"
            : unauthorizedMessage("transactional")
      }
      onDragStart={(event) => {
        // 実体は画面側の state に持つ。dragover 中は dataTransfer を読めないため
        // （[[RowDragHandle]] と同じ）。ここに入れるのは外へ落としたときの保険。
        event.dataTransfer.setData("text/plain", entry.project_number);
        event.dataTransfer.effectAllowed = "move";
        onDragStart(entry);
      }}
      onDragEnd={onDragEnd}
      onKeyDown={onKeyDown}
      className={[
        "group rounded-md border border-default-200 border-l-4 bg-white px-1.5 py-1 text-xs shadow-sm",
        accent,
        shipped ? "text-default-400" : "text-default-700",
        canMove ? "cursor-grab active:cursor-grabbing" : "",
        isMoving ? "opacity-50" : "",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
      ].join(" ")}
    >
      <div className="flex items-center gap-1">
        <Link
          to={`/projects/${entry.project_id}`}
          className="truncate font-bold text-brand-500 underline underline-offset-2 hover:text-brand-600"
          // リンクは既定でドラッグできてしまい、カードのドラッグを奪う。
          draggable={false}
        >
          {entry.project_number}
        </Link>
        {entry.is_set_product && (
          <Layers
            size={12}
            className="shrink-0 text-default-400"
            aria-label="セット品"
          />
        )}
        {shipped && (
          <Truck size={12} className="shrink-0 text-default-400" aria-label="出荷済" />
        )}
        <Dropdown placement="bottom-end">
          <DropdownTrigger>
            <button
              type="button"
              aria-label={`${entry.project_number} の操作`}
              className="ml-auto shrink-0 rounded p-0.5 text-default-400 hover:bg-default-100 hover:text-default-600"
            >
              <MoreVertical size={14} />
            </button>
          </DropdownTrigger>
          <DropdownMenu aria-label={`${entry.project_number} の操作`}>
            <DropdownItem
              key="date"
              isDisabled={!canMove}
              description={
                shipped
                  ? "出荷済みのため変更できません"
                  : !editable
                    ? unauthorizedMessage("transactional")
                    : undefined
              }
              onPress={() => onEditDate(entry)}
            >
              出荷予定日を変更
            </DropdownItem>
            <DropdownItem key="open" href={`/projects/${entry.project_id}`}>
              案件を開く
            </DropdownItem>
          </DropdownMenu>
        </Dropdown>
      </div>
      <div className="flex items-center gap-1">
        <span className="truncate">
          {destination ?? "得意先未設定"}
          {entry.ship_to_name && (
            <span className="ml-1 text-[10px] text-default-400">直送</span>
          )}
        </span>
        {/* 案件の一部だけがこの日に出る（分納）ときは 1/3 のように出す。
            明細がまだ 1 行も無い案件に素の「0」を出すと数量に見えるので、
            行数ではなく状態として書く。 */}
        <span className="ml-auto shrink-0 whitespace-nowrap tabular-nums text-default-500">
          {entry.project_item_count === 0
            ? "明細なし"
            : entry.item_count === entry.project_item_count
              ? `${entry.item_count} 行`
              : `${entry.item_count}/${entry.project_item_count} 行`}
        </span>
      </div>
    </div>
  );
}
